import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Send, Loader2, Star, Shield, Clock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

const LOGO_URL = "/logo-mbs.png";
const BASE_URL = "https://monbeausite.com";

const FALLBACK_AVATARS = [
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&h=120&fit=crop&crop=faces",
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&h=120&fit=crop&crop=faces",
  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&h=120&fit=crop&crop=faces",
];

const TRUST_POINTS = [
  { Icon: Sparkles, label: "Démo en 7 jours", desc: "Vous voyez votre futur site avant de payer." },
  { Icon: Shield, label: "Sans engagement", desc: "Pas de carte bancaire à l'envoi du formulaire." },
  { Icon: Clock, label: "Réponse < 48h", desc: "Nous vous rappelons personnellement." },
];

export default function DemoRequestPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nom_entreprise: "",
    nom_responsable: "",
    email: "",
    telephone: "",
    message: "",
    consent_contact: false,
    consent_marketing: false,
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      // Push GA event for conversion tracking (Google Ads attendra cet event)
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          form_location: "/demande-de-demo",
          value: 1,
          currency: "EUR",
        });
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue. Réessayez ou écrivez-moi directement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBF9F5]">
      <Helmet>
        <title>Demande de démo gratuite — Votre site internet professionnel | MonBeauSite</title>
        <meta
          name="description"
          content="Recevez gratuitement une démo de votre futur site internet en 7 jours. Sans engagement, sans carte bancaire. Forfaits dès 39€/mois pour artisans, commerçants et indépendants."
        />
        <link rel="canonical" href={`${BASE_URL}/demande-de-demo`} />
        <meta property="og:title" content="Demande de démo gratuite — MonBeauSite" />
        <meta property="og:description" content="Recevez votre démo gratuite en 7 jours. Sans engagement." />
        <meta property="og:url" content={`${BASE_URL}/demande-de-demo`} />
        <meta property="og:type" content="website" />
        {/* Schema.org : page de contact / formulaire */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Demande de démo gratuite — MonBeauSite",
            url: `${BASE_URL}/demande-de-demo`,
            description: "Formulaire de demande de démonstration gratuite d'un site internet professionnel.",
            potentialAction: {
              "@type": "CommunicateAction",
              target: `${BASE_URL}/demande-de-demo`,
              name: "Demander une démo gratuite",
            },
          })}
        </script>
      </Helmet>

      {/* Minimal header */}
      <header className="border-b border-[#BFBFBF]/50 bg-white/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="demo-page-home-link">
            <img src={LOGO_URL} alt="MonBeauSite" className="h-7" />
          </Link>
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-xs nav-text tracking-wider uppercase text-[#4A4A4A] hover:text-[#C46A4A] transition-colors"
            data-testid="demo-page-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Retour à l'accueil
          </button>
        </div>
      </header>

      {/* Hero + Form layout */}
      <main className="max-w-6xl mx-auto px-6 py-12 lg:py-20 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        {/* Left column : Marketing copy */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:sticky lg:top-28"
        >
          <div className="flex items-center gap-3 mb-6 opacity-70" aria-hidden="true">
            <span className="h-px w-10 bg-gradient-to-r from-transparent to-[#D4AF37]/60" />
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/70" />
          </div>
          <p className="section-num font-mono-tech text-xs tracking-widest text-[#C46A4A] mb-4 inline-flex">DÉMO // GRATUITE</p>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1F3535] leading-[1.15] mb-6 font-montserrat tracking-tight"
          >
            Voyez votre futur site internet{" "}
            <span className="font-playfair italic text-[#C46A4A] font-normal">avant même de payer.</span>
          </h1>
          <p className="text-[#4A4A4A] text-base sm:text-lg font-body leading-relaxed mb-8 max-w-lg">
            Nous créons gratuitement une démo de votre site, à votre image. Vous la découvrez en 7 jours, vous la validez (ou pas), et vous lancez votre activité en ligne avec un site qui vous ressemble vraiment.
          </p>

          {/* Trust points */}
          <ul className="space-y-4 mb-8" data-testid="demo-trust-points">
            {TRUST_POINTS.map(({ Icon, label, desc }, i) => (
              <li key={i} className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#C46A4A]/10 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-[#C46A4A]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1F3535] font-body">{label}</p>
                  <p className="text-sm text-[#6B6B6B] font-body leading-relaxed">{desc}</p>
                </div>
              </li>
            ))}
          </ul>

          {/* Mini social proof */}
          <div className="flex items-center gap-3 pt-6 border-t border-[#D4AF37]/15">
            <div className="flex -space-x-2">
              {FALLBACK_AVATARS.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  loading="lazy"
                  className="w-8 h-8 rounded-full border-2 border-[#FBF9F5] object-cover"
                />
              ))}
            </div>
            <div>
              <div className="flex gap-0.5 mb-0.5" aria-label="5 étoiles">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-3 h-3 fill-[#3F6B6B] text-[#3F6B6B]" />
                ))}
              </div>
              <p className="text-xs text-[#6B6B6B] font-body">Plus de 30 entrepreneurs nous ont déjà fait confiance.</p>
            </div>
          </div>
        </motion.div>

        {/* Right column : Form */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white rounded-2xl border border-[#BFBFBF] shadow-xl shadow-[#1F3535]/5 p-7 sm:p-9"
        >
          {sent ? (
            <div className="text-center py-6" data-testid="demo-success">
              <div className="w-16 h-16 rounded-full bg-[#3F6B6B]/10 flex items-center justify-center mx-auto mb-5">
                <Check className="w-7 h-7 text-[#3F6B6B]" />
              </div>
              <h2
                className="text-2xl font-medium text-[#1F3535] mb-3 font-montserrat tracking-tight"
              >
                Demande <span className="font-playfair italic text-[#3F6B6B] font-normal">envoyée !</span>
              </h2>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mb-2">
                Merci <strong>{form.nom_responsable}</strong>. Nous revenons vers vous personnellement sous 48h.
              </p>
              <p className="text-xs text-[#6B6B6B] mb-7">
                En attendant, jetez un œil à nos articles ou à la FAQ — vous y trouverez sûrement déjà quelques réponses.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/blog"
                  className="inline-flex items-center justify-center gap-2 bg-[#2F4F4F] text-white hover:bg-[#1F3535] px-6 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wide transition-colors"
                >
                  Lire le blog
                </Link>
                <Link
                  to="/faq"
                  className="inline-flex items-center justify-center gap-2 bg-white border border-[#BFBFBF] text-[#1F3535] hover:border-[#D4AF37]/60 px-6 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wide transition-colors"
                >
                  Voir la FAQ
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2
                  className="text-2xl sm:text-3xl font-medium text-[#1F3535] mb-2 font-montserrat tracking-tight"
                >
                  Demandez votre <span className="font-playfair italic text-[#C46A4A] font-normal">démo gratuite</span>
                </h2>
                <p className="text-sm text-[#6B6B6B] leading-relaxed">
                  Parlez-nous brièvement de votre activité. Nous revenons vers vous sous 48h.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-page-form">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Entreprise *</Label>
                    <Input
                      data-testid="demo-page-nom-entreprise"
                      value={form.nom_entreprise}
                      onChange={(e) => update("nom_entreprise", e.target.value)}
                      className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] h-11 text-sm text-[#333333] placeholder:text-[#999]"
                      placeholder="Votre entreprise"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Responsable *</Label>
                    <Input
                      data-testid="demo-page-nom-responsable"
                      value={form.nom_responsable}
                      onChange={(e) => update("nom_responsable", e.target.value)}
                      className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] h-11 text-sm text-[#333333] placeholder:text-[#999]"
                      placeholder="Votre nom"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Email *</Label>
                  <Input
                    data-testid="demo-page-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] h-11 text-sm text-[#333333] placeholder:text-[#999]"
                    placeholder="votre@email.com"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Téléphone</Label>
                  <Input
                    data-testid="demo-page-telephone"
                    type="tel"
                    value={form.telephone}
                    onChange={(e) => update("telephone", e.target.value)}
                    className="mt-1 bg-[#FBF9F5] border-[#BFBFBF] h-11 text-sm text-[#333333] placeholder:text-[#999]"
                    placeholder="06 00 00 00 00"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider">Votre projet en quelques mots</Label>
                  <textarea
                    data-testid="demo-page-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    className="mt-1 w-full bg-[#FBF9F5] border border-[#BFBFBF] rounded-md min-h-[96px] px-3 py-2.5 text-sm text-[#333333] placeholder:text-[#999] resize-none focus:outline-none focus:ring-2 focus:ring-[#2F4F4F]/20 focus:border-[#2F4F4F]/40"
                    placeholder="Décrivez brièvement votre activité et ce que vous recherchez…"
                  />
                </div>

                {/* Consent checkboxes (RGPD) */}
                <div className="space-y-3 pt-2">
                  <label className="flex items-start gap-2.5 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={form.consent_contact}
                      onChange={(e) => update("consent_contact", e.target.checked)}
                      data-testid="demo-page-consent-contact"
                      className="mt-0.5 w-4 h-4 rounded border-[#BFBFBF] text-[#2F4F4F] focus:ring-[#2F4F4F]/30 accent-[#2F4F4F] flex-shrink-0"
                      required
                    />
                    <span className="text-[11px] text-[#6B6B6B] leading-relaxed group-hover:text-[#333333] transition-colors">
                      J'accepte que mes données soient utilisées pour être recontacté au sujet de mon projet. *
                      {" "}
                      <Link to="/politique-confidentialite" className="text-[#2F4F4F] underline">
                        Politique de confidentialité
                      </Link>
                    </span>
                  </label>
                  <label className="flex items-start gap-2.5 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={form.consent_marketing}
                      onChange={(e) => update("consent_marketing", e.target.checked)}
                      data-testid="demo-page-consent-marketing"
                      className="mt-0.5 w-4 h-4 rounded border-[#BFBFBF] text-[#2F4F4F] focus:ring-[#2F4F4F]/30 accent-[#2F4F4F] flex-shrink-0"
                    />
                    <span className="text-[11px] text-[#6B6B6B] leading-relaxed group-hover:text-[#333333] transition-colors">
                      J'accepte de recevoir les conseils d'expert pour optimiser ma présence en ligne et faire grandir mon activité (facultatif).
                    </span>
                  </label>
                </div>

                <Button
                  data-testid="demo-page-submit"
                  type="submit"
                  disabled={loading || !form.consent_contact}
                  className="w-full bg-[#C46A4A] text-white hover:bg-[#A6553A] h-12 rounded-full font-semibold tracking-wide uppercase text-xs shadow-md shadow-[#C46A4A]/20"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> Lancer ma démo gratuite
                    </>
                  )}
                </Button>
                <p className="text-center text-[10px] text-[#999] pt-1">
                  Réponse rapide. Sans aucun engagement.
                </p>
              </form>
            </>
          )}
        </motion.div>
      </main>

      {/* Minimal footer */}
      <footer className="border-t border-[#BFBFBF] py-8 bg-white mt-auto">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-body">
          <p className="text-[#6B6B6B]/70">
            MonBeauSite | Tous droits réservés
          </p>
          <div className="flex gap-5">
            <Link to="/cgv" className="text-[#6B6B6B]/70 hover:text-[#C46A4A] transition-colors">CGV</Link>
            <Link to="/mentions-legales" className="text-[#6B6B6B]/70 hover:text-[#C46A4A] transition-colors">Mentions légales</Link>
            <Link to="/politique-confidentialite" className="text-[#6B6B6B]/70 hover:text-[#C46A4A] transition-colors">Confidentialité</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
